<template>
  <li class="utterance-list-item">
    <div class="utterance-item">
      <input
        :id="'input-' + utterance.id"
        ref="inputElement"
        v-model="localUtterance.text"
        class="input"
        type="text"
        :placeholder="placeholderText"
        required
        @keydown.enter="$emit('move-to-next', utterance.id)"
        @keyup="$v.localUtterance.text.$touch()"
        :disabled="!isEditable"
      />
      <a class="delete is-small" @click="$emit('delete', utterance.id)"></a>
    </div>
    <p class="help is-danger" v-if="isTextRequiredInvalid">The {{ term }} text must not be empty</p>
    <p class="help is-danger" v-if="isTextUniqueInvalid">The {{ term }} must be unique</p>
  </li>
</template>

<script lang="ts" setup>
import { UtteranceViewModel } from '@/features/bots/models/intents/utteranceViewModel';
import { computed, PropType, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import VuelidateUtil from '@shared/util/vuelidateUtil';

const props = defineProps({
  utterance: {
    type: Object as PropType<UtteranceViewModel>,
    required: true,
  },
  allUtterances: {
    type: Array as PropType<UtteranceViewModel[]>,
    required: true,
  },
  term: {
    type: String,
    default: 'utterance',
  },
  isEditable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (e: 'update:utterance', value: UtteranceViewModel): void;
  (e: 'delete', id: string): void;
  (e: 'move-to-next', id: string): void;
}>();

const localUtterance = computed({
  get: () => props.utterance,
  set: (value) => emit('update:utterance', value),
});

const inputElement = ref<HTMLInputElement | null>(null);

const placeholderText = computed(() => `Enter your ${props.term}`);

const validations = {
  localUtterance: {
    text: {
      required,
      isUnique(value: string) {
        return props.allUtterances.filter((u) => u.text === value).length <= 1;
      },
    },
  },
};

const $v = useVuelidate(validations, { localUtterance });
const isTextRequiredInvalid = computed(() => VuelidateUtil.isRequiredInvalid($v, (v) => v.localUtterance.text));
const isTextUniqueInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.localUtterance.text, 'isUnique'),
);

defineExpose({
  focus: () => inputElement.value?.focus(),
});
</script>

<style scoped lang="scss">
.utterance-list-item {
  padding: 0.25rem 0.5rem;
}

.utterance-item {
  background: #dbdfe8;
  border-radius: 3px;
  padding: 0.25rem 1rem;
  position: relative;
  border: solid 1px transparent;

  &:focus-within {
    border: solid 1px $color-netcall-deep-sea;
    box-shadow: 0 1px 8px 1px rgba(0, 0, 0, 0.2);
  }

  .input {
    background-color: transparent;
    border: none;
    box-shadow: none;
    padding: 0;
  }

  .delete {
    position: absolute;
    top: 5px;
    right: 5px;
  }
}
</style>
